import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { sortTags, isTagSort, type TagSort } from "@media-viewer/shared";

interface TagsProps {
  isAdmin: boolean;
}

const SORT_LABELS: Record<TagSort, string> = {
  most: "Most tagged",
  least: "Least tagged",
  alpha: "A–Z",
};
const SORT_KEY = "mv-tags-sort";

function initialSort(): TagSort {
  try {
    const ls = window.localStorage.getItem(SORT_KEY);
    if (isTagSort(ls)) return ls;
  } catch {
    /* ignore */
  }
  return "most";
}

export function Tags({ isAdmin }: TagsProps) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tags"], queryFn: api.tags });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<TagSort>(initialSort);

  useEffect(() => {
    try {
      window.localStorage.setItem(SORT_KEY, sort);
    } catch {
      /* ignore */
    }
  }, [sort]);

  const del = useMutation({
    mutationFn: ({ tagId }: { tagId: number }) => api.deleteTag(tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["media"] });
    },
  });

  if (q.isPending) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (q.isError) return <div className="p-8 text-red-300">Error: {String(q.error)}</div>;

  const trimmed = search.trim().toLowerCase();
  const visible = sortTags(
    q.data.filter((t) => (trimmed ? t.name.toLowerCase().includes(trimmed) : true)),
    sort,
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-semibold">Tags</h2>
        <span className="text-xs text-zinc-500">{q.data.length.toLocaleString()} total</span>
        <div className="ml-auto flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tags…"
            className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-sm focus:border-zinc-500 focus:outline-none"
          />
          <label className="flex items-center gap-1 text-xs text-zinc-400">
            <span className="hidden sm:inline">Sort</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as TagSort)}
              className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-700 focus:border-zinc-500 focus:outline-none"
              aria-label="Sort tags"
            >
              {(Object.keys(SORT_LABELS) as TagSort[]).map((o) => (
                <option key={o} value={o}>
                  {SORT_LABELS[o]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {q.data.length === 0 ? (
        <p className="text-zinc-500">No tags yet. Create some from a media detail view.</p>
      ) : visible.length === 0 ? (
        <p className="text-zinc-500">No tags match "{search.trim()}".</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {visible.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 rounded border border-zinc-800 bg-zinc-900 text-sm hover:border-zinc-700"
            >
              <a href={`/tags/${t.slug}`} className="flex items-center gap-2 px-3 py-1.5">
                <span>{t.name}</span>
                <span className="text-xs text-zinc-500">{t.count ?? 0}</span>
              </a>
              {isAdmin && (
                <button
                  onClick={() => {
                    const count = t.count ?? 0;
                    if (
                      window.confirm(
                        `Delete tag "${t.name}"?\n\n${
                          count > 0
                            ? `It is currently applied to ${count.toLocaleString()} item${count === 1 ? "" : "s"} — they will be untagged but not deleted.`
                            : "It is not currently applied to anything."
                        }`,
                      )
                    ) {
                      del.mutate({ tagId: t.id });
                    }
                  }}
                  disabled={del.isPending}
                  className="rounded-r border-l border-zinc-800 px-2 py-1.5 text-xs text-zinc-500 hover:bg-rose-900/40 hover:text-rose-200 disabled:opacity-40"
                  title="Delete tag (admin)"
                  aria-label={`Delete tag ${t.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
